/**
 * Global "energy" bus that drives reactive gradient visuals (PlumeField, aura).
 *
 * Sources push energy — typing keystrokes today; when voice notes land, call
 * `pushEnergy(level)` per audio frame with the normalized mic amplitude.
 *
 * Tuning (locked in the plume design study):
 * - Pushes are saturating: the fuller the level, the less each push adds,
 *   so max energy takes a sustained sentence of typing, not three taps.
 * - The visible level chases the pushed target with a slow asymmetric lerp
 *   (patient rise, quicker settle) and fully drains in ~2.3s of silence.
 * - `pushEnergy` also fans out a "kick" event so plume consumers can throw
 *   per-keystroke randomized impulses.
 *
 * Consumers subscribe and mutate DOM styles directly — nothing re-renders
 * through React while energy decays.
 */

type Listener = (level: number) => void

const levelListeners = new Set<Listener>()
const kickListeners = new Set<Listener>()

let target = 0
let level = 0
let rafId: number | null = null
let lastTs: number | null = null

const DECAY_PER_MS = 1 / 2300 // full drain ~2.3s
const RISE_K = 0.045 // per ~16.7ms frame
const FALL_K = 0.032

function emitLevel() {
  for (const listener of levelListeners) listener(level)
}

function tick(ts: number) {
  const dt = lastTs === null ? 16.7 : Math.min(50, ts - lastTs)
  lastTs = ts
  target = Math.max(0, target - dt * DECAY_PER_MS)
  const k = target > level ? RISE_K : FALL_K
  level += (target - level) * k * (dt / 16.7)
  if (target === 0 && level < 0.001) {
    level = 0
    emitLevel()
    rafId = null
    lastTs = null
    return
  }
  emitLevel()
  rafId = requestAnimationFrame(tick)
}

/** Bump the energy. Keystrokes use the default; voice should pass its normalized amplitude. */
export function pushEnergy(amount = 0.1) {
  target = Math.min(1, target + amount * (1 - target * 0.78))
  for (const listener of kickListeners) listener(level)
  if (rafId === null) rafId = requestAnimationFrame(tick)
}

/**
 * Slam the energy near max in one hit — for moments where a whole prompt lands
 * at once (picking a topic suggestion) rather than keystroke-by-keystroke.
 * The level jumps immediately (no patient rise) and then decays normally.
 */
export function burstEnergy(amount = 0.95) {
  const a = Math.min(1, amount)
  target = Math.max(target, a)
  level = Math.max(level, a * 0.85)
  for (const listener of kickListeners) listener(level)
  if (rafId === null) rafId = requestAnimationFrame(tick)
}

/** Smoothed 0..1 level, emitted every frame while energy is live (and once on subscribe). */
export function subscribeEnergy(listener: Listener): () => void {
  levelListeners.add(listener)
  listener(level)
  return () => {
    levelListeners.delete(listener)
  }
}

/** Fires once per pushEnergy call — use for per-keystroke impulses. */
export function subscribeEnergyKick(listener: Listener): () => void {
  kickListeners.add(listener)
  return () => {
    kickListeners.delete(listener)
  }
}
